<template>
  <div>
    <v-dialog
      v-model="showShippingDialog"
      persistent
      max-width="540"
    >
      <ValidationObserver ref="shippingForm" v-slot="{ invalid }">
        <v-form @submit.prevent="redeemReward">
          <v-card>
            <v-card-text v-if="isInstant">
              <div
                class="text-left"
                v-html="bounty.redeem_description"
              ></div>
            </v-card-text>
            <v-card-text v-else>
              <v-col>
                <v-row>
                  <v-col cols="auto" v-if="pictures.length">
                    <v-avatar :size="75">
                      <v-img :src="pictures[0]" />
                    </v-avatar>
                  </v-col>
                  <v-col>
                    <div class="">
                      <span class="title text--primary">
                        {{bounty.name}}
                      </span>
                    </div>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="mt-4 pb-0">
                    <div class="body-2">
                      {{ $t('bounties.shippingModalDescription') }}
                    </div>
                  </v-col>
                </v-row>
                <v-row
                  v-for="field in address"
                  :key="field.key"
                >
                  <v-col>
                    <country-field
                      v-if="field.key === 'country'"
                      v-model="field.value"
                      :field="{
                        name: field.key,
                        placeholder: field.label,
                        required: field.required,
                        disabled: !field.editable
                      }"
                      :value="field.value"
                      :emit-only-country-code="true"
                      class="mx-0"
                    />
                    <generic-input
                      v-else
                      v-model="field.value"
                      :component="{
                        name: field.key,
                        label: field.label,
                        disabled: !field.editable,
                        rules: field.required ? 'required' : ''
                      }"
                      :value="field.value"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-card-text>
            <v-card-actions class="px-6 pb-6">
              <v-spacer></v-spacer>
              <v-btn type="button" text @click="closeFormDialog">
                {{ $t('cancel') }}
              </v-btn>
              <v-btn
                type="submit"
                color="primary"
                :disabled="invalid || loading"
                :loading="loading"
              >
                {{ $t('bounties.redeemBountyDetailsRedeem') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
      </ValidationObserver>
    </v-dialog>
    <v-dialog
      v-model="showSuccessShipping"
      persistent
      max-width="320"
    >
      <v-card>
        <v-card-title>
          {{ $t('bounties.shippingRedeemedSuccessHeader') }}
        </v-card-title>
        <v-card-text>
          {{ bounty.redeem_success_alert_text }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            type="button"
            color="primary"
            @click="showSuccessShipping = false"
          >
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="showErrorShipping"
      persistent
      max-width="320"
    >
      <v-card>
        <v-card-title>
          {{ $t('bounties.shippingRedeemedError') }}
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            type="button"
            color="primary"
            @click="showErrorShipping = false"
          >
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import {mapGetters, mapActions} from 'vuex'
import GenericInput from '@/components/generic/GenericInput'
import CountryField from '@/components/auth/fields/CountryField'

const SUPPORTED_FIELDS = [
  'street',
  'street_number',
  'city',
  'city_code',
  'state',
  'country'
]
const MANDATORY_FIELDS = ['street', 'city', 'city_code']

export default {
  name: 'ShippingDialog',
  components: {
    GenericInput,
    CountryField,
  },
  props: {
    bounty: {
      type: Object,
      required: true,
    },
    showShippingDialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showSuccessShipping: false,
      showErrorShipping: false,
      loading: false,
    }
  },
  computed: {
    ...mapGetters(['user', 'authSettings']),
    userAddress() {
      return this.user && this.user.address ? this.user.address : {}
    },
    address() {
      const filteredFields = SUPPORTED_FIELDS.reduce((fields, currentKey) => {
        const attribute = _.get(this.authSettings, `attributes.${currentKey}`)
        const isMandatory = MANDATORY_FIELDS.includes(currentKey)

        if (
          !_.isEmpty(attribute) && (_.get(attribute, 'show_in_app') || isMandatory)
        ) {
          fields.push({
            key: currentKey,
            value: _.get(this.userAddress, currentKey, ''),
            label: this.getFieldLabel(currentKey, attribute.required),
            required: isMandatory || attribute.required,
            editable: attribute.editable,
          })
        }

        return fields
      }, [])

      if (_.isEmpty(filteredFields)) {
        return []
      }

      // Temporary solution to show the street number just after the street since we don't have an "order" prop for these fields
      return _.sortBy(filteredFields, (field) => {
        if (field.key === 'street') {
          return 0
        } else if (field.key === 'street_number') {
          return 1
        } else {
          return 2
        }
      })
    },
    pictures() {
      if (!_.isEmpty(this.bounty.pictures)) {
        return this.bounty.pictures.map((picture) => picture.image)
      }
      if (_.get(this.bounty, 'image', null)) {
        return [this.bounty.image]
      }
      return []
    },
    isInstant() {
      return _.get(this.bounty, 'instant')
    }
  },
  methods: {
    ...mapActions(['redeemShippingReward']),
    async redeemReward() {
      const result = await this.$refs.shippingForm.validate()

      if (!result) {
        return
      }

      this.loading = true
      try {
        const payload = this.address.reduce((payloadObj, currentField) => {
          payloadObj[currentField.key] = currentField.value
          return payloadObj
        }, {})

        const res = await this.redeemShippingReward({
          bounty_id: this.bounty.id,
          ...payload,
        })

        if (res.success) {
          this.showSuccessShipping = true
        } else {
          this.showErrorShipping = true
        }
      } catch (error) {
        this.showErrorShipping = true
      } finally {
        this.loading = false
        this.closeFormDialog()
      }
    },
    getFieldLabel(fieldKey, required) {
      const isMandatory = MANDATORY_FIELDS.includes(fieldKey)
      const requiredHint = fieldKey !== 'country' && (isMandatory || required) ? ' *' : ''

      return this.$t(`bounties.${fieldKey}`) + requiredHint
    },
    closeFormDialog() {
      this.$emit('finish-pressed')
    }
  }
}
</script>

<style lang="scss" scoped>
.v-card__title {
  flex-wrap: wrap;

  h3 {
    width: 100%;
  }
}
</style>
